import { useState } from 'react'
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import { Card, CardContent, CardDescription, CardHeader } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { DotPattern } from '@/components/ui/dot-pattern'
import { Logo } from '@/components/ui/logo'
import { Lock, ArrowRight, Loader2, AlertCircle } from 'lucide-react'
import { SuporteService } from '@/services/suporte-service'

export const Route = createFileRoute('/suporte-login')({
  beforeLoad: () => {
    if (typeof window === 'undefined') return
    if (SuporteService.verificarAcesso()) {
      throw redirect({ to: '/suporte' })
    }
  },
  component: SuporteLogin,
})

function SuporteLogin() {
  const navigate = useNavigate()
  const [token, setToken] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!token.trim()) return

    setLoading(true)
    try {
      const isValid = await SuporteService.verificarToken(token)
      if (isValid) {
        SuporteService.salvarAcesso(token)
        navigate({ to: '/suporte' })
      } else {
        setError('Senha de suporte inválida.')
      }
    } catch {
      setError('Erro de conexão.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-gradient-to-br from-slate-900 to-slate-800 text-white relative">
      <DotPattern className="absolute inset-0 opacity-20 text-white" />

      <div className="w-full max-w-md relative z-10">
        <Card className="bg-slate-950/50 border-slate-800 backdrop-blur-xl text-white shadow-2xl">
          <CardHeader className="text-center space-y-4">
            <div className="mx-auto h-12 opacity-80 mix-blend-screen">
              <Logo className="h-12" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold">Painel de Suporte</h1>
              <CardDescription className="text-slate-400">Acesso restrito para equipe de atendimento</CardDescription>
            </div>
          </CardHeader>

          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <label className="text-sm font-medium flex items-center gap-2">
                  Senha Mestra <Lock className="w-4 h-4 text-slate-400" />
                </label>
                <Input
                  type="password"
                  placeholder="Digite a senha de suporte"
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                  disabled={loading}
                  className="bg-slate-900 border-slate-700 text-white"
                />
              </div>

              {error && (
                <div className="flex items-start gap-2 text-sm text-red-400 bg-red-400/10 p-3 rounded-md border border-red-400/20">
                  <AlertCircle className="h-4 w-4 mt-0.5" />
                  <span>{error}</span>
                </div>
              )}

              <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700" disabled={loading || !token}>
                {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : 'Acessar'}
                {!loading && <ArrowRight className="ml-2 h-4 w-4" />}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}